import React, { useState, useEffect } from 'react';
import { Form, Alert, Spinner } from 'react-bootstrap';
import ModalCustom from '../../UI/modal';
import Button from '../../UI/button';
import { useTripModal } from '../../contexts/modal-context-trip';
import { useTrip } from '../../contexts/trip-context';
import tripsService from '../../../services/trips/trips.service';
import tripFolderService from '../../../services/trips/trip-folder.service';
import { ITripModelSummary } from '../../../Models/trips/trip-summary.model';

const MoveFolderToTripModal: React.FC = () => {
  const { modalState, closeModal } = useTripModal();
  const { setRefreshTrip } = useTrip();
  const [trips, setTrips] = useState<ITripModelSummary[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [fetchingTrips, setFetchingTrips] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isOpen = modalState.show && modalState.modalType === 'moveFolderToTrip';
  const { folderId, folderName, destinationName, sourceTripId } =
    modalState.data || {};

  const fetchTrips = React.useCallback(async () => {
    setFetchingTrips(true);
    setError(null);
    try {
      const fetchedTrips = await tripsService.getTrips({
        field: 'createdAt',
        direction: 'desc',
        queryString: ''
      });
      // Filter out the source trip
      const availableTrips = fetchedTrips.filter(
        (trip: ITripModelSummary) => trip.id !== sourceTripId
      );
      setTrips(availableTrips);
    } catch (err: any) {
      console.error('Error fetching trips:', err);
      setError('Failed to load trips. Please try again.');
    } finally {
      setFetchingTrips(false);
    }
  }, [sourceTripId]);

  useEffect(() => {
    if (isOpen) {
      fetchTrips();
    }
  }, [isOpen, fetchTrips]);

  const handleSubmit = async () => {
    if (!selectedTripId || !folderId || !sourceTripId) {
      setError('Please select a trip');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await tripFolderService.copyFolderToTrip(
        sourceTripId,
        selectedTripId,
        folderId
      );

      // Refresh the trip data to show the changes
      setRefreshTrip(true);

      handleClose();
    } catch (err: any) {
      console.error('Error moving folder to trip:', err);
      setError(err?.message || 'Failed to move folder. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTripId('');
    setError(null);
    closeModal();
  };

  return (
    <ModalCustom
      show={isOpen}
      onHide={() => handleClose()}
      title='Move Folder to Trip'
    >
      <div className='p-3'>
        {fetchingTrips ? (
          <div className='text-center py-4'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            {error && (
              <Alert
                variant='danger'
                className='mb-3'
              >
                {error}
              </Alert>
            )}

            <div className='mb-3'>
              <p className='text-muted mb-1'>Moving folder:</p>
              <p className='fw-bold mb-0'>{folderName || 'Unnamed Folder'}</p>
              {destinationName && (
                <p className='text-muted small mb-0'>
                  From destination: {destinationName}
                </p>
              )}
            </div>

            <Alert
              variant='info'
              className='mb-3'
            >
              This will copy the folder, its activities, and the destination to
              the selected trip. Activities will be added to the itinerary on
              day 1.
            </Alert>

            <Form.Group className='mb-3'>
              <Form.Label>Select Target Trip</Form.Label>
              <Form.Select
                value={selectedTripId}
                onChange={(e) => setSelectedTripId(e.target.value)}
                disabled={loading || trips.length === 0}
              >
                <option value=''>
                  {trips.length === 0
                    ? 'No other trips available'
                    : 'Choose a trip...'}
                </option>
                {trips.map((trip) => (
                  <option
                    key={trip.id}
                    value={trip.id}
                  >
                    {trip.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <div className='d-flex justify-content-end gap-2'>
              <Button
                onClick={handleClose}
                variant='outline-secondary'
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant='primary'
                disabled={loading || !selectedTripId || fetchingTrips}
              >
                {loading ? (
                  <>
                    <Spinner
                      animation='border'
                      size='sm'
                      className='me-2'
                    />
                    Moving...
                  </>
                ) : (
                  'Move Folder'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </ModalCustom>
  );
};

export default MoveFolderToTripModal;
